import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { ResellerProvider } from "./context/ResellerContext";

// Pages
import Landing from "./pages/Landing";
import SmartLanding from "./pages/SmartLanding";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import Jobs from "./pages/Jobs";
import JobCreate from "./pages/JobCreate";
import JobDetail from "./pages/JobDetail";
import Team from "./pages/Team";
import Branches from "./pages/Branches";
import Settings from "./pages/Settings";
import Inventory from "./pages/Inventory";
import Metrics from "./pages/Metrics";
import Customers from "./pages/Customers";
import ProfitReports from "./pages/ProfitReports";
import Billing from "./pages/Billing";
import Contact from "./pages/Contact";
import SuperAdminLogin from "./pages/SuperAdminLogin";
import SuperAdminDashboard from "./pages/SuperAdminDashboard";
import PublicTrack from "./pages/PublicTrack";
import LegalPage from "./pages/LegalPage";

// Trading Modules
import MobileTrading from "./pages/MobileTrading";
import ITEquipment from "./pages/ITEquipment";
import BuyDevice from "./pages/BuyDevice";
import DeviceDetail from "./pages/DeviceDetail";
import DeclinedIntakes from "./pages/DeclinedIntakes";
import CustomerIntakes from "./pages/CustomerIntakes";

// SEO Pages
import ComparePage from "./pages/ComparePage";
import FeaturePage from "./pages/FeaturePage";
import CityLandingPage from "./pages/CityLandingPage";

// Appointment System
import AppointmentSettings from "./pages/AppointmentSettings";
import Bookings from "./pages/Bookings";

// Customer Portal (Public)
import CustomerPortal from "./pages/customer/CustomerPortal";
import BookAppointment from "./pages/customer/BookAppointment";
import TrackJob from "./pages/customer/TrackJob";
import RepairHistory from "./pages/customer/RepairHistory";
import CustomerIntake from "./pages/customer/CustomerIntake";

// Reseller Portal
import ResellerLogin from "./pages/ResellerLogin";
import ResellerRegister from "./pages/ResellerRegister";
import ResellerDashboard from "./pages/ResellerDashboard";

// Retailer Portal (B2B)
import { RetailerAuthProvider } from "./context/RetailerAuthContext";
import RetailerAuth from "./pages/retailer/RetailerAuth";
import RetailerLayout from "./pages/retailer/RetailerLayout";
import RetailerDashboard from "./pages/retailer/RetailerDashboard";
import ForwardJob from "./pages/retailer/ForwardJob";
import RetailerJobs from "./pages/retailer/RetailerJobs";
import PartnerShops from "./pages/retailer/PartnerShops";

// SEO Component
import SEOHead from "./components/SEOHead";

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner w-8 h-8 border-primary" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Admin Route wrapper
const AdminRoute = ({ children }) => {
  const { isAuthenticated, isAdmin, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner w-8 h-8 border-primary" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Public Route wrapper (redirect to dashboard if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner w-8 h-8 border-primary" />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <>
      {/* Dynamic SEO - Updates canonical URL based on current route */}
      <SEOHead />
      
      <Routes>
        {/* Smart Landing - Detects customer subdomain vs main domain */}
        <Route path="/" element={<SmartLanding />} />
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/signup"
        element={
          <PublicRoute>
            <Signup />
          </PublicRoute>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <PublicRoute>
            <ForgotPassword />
          </PublicRoute>
        }
      />
      <Route
        path="/reset-password"
        element={<ResetPassword />}
      />

      {/* Protected Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/jobs"
        element={
          <ProtectedRoute>
            <Jobs />
          </ProtectedRoute>
        }
      />
      <Route
        path="/jobs/new"
        element={
          <ProtectedRoute>
            <JobCreate />
          </ProtectedRoute>
        }
      />
      <Route
        path="/jobs/:id"
        element={
          <ProtectedRoute>
            <JobDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        }
      />

      {/* Admin Routes */}
      <Route
        path="/appointment-settings"
        element={
          <AdminRoute>
            <AppointmentSettings />
          </AdminRoute>
        }
      />
      <Route
        path="/bookings"
        element={
          <ProtectedRoute>
            <Bookings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/team"
        element={
          <AdminRoute>
            <Team />
          </AdminRoute>
        }
      />
      <Route
        path="/branches"
        element={
          <AdminRoute>
            <Branches />
          </AdminRoute>
        }
      />

      {/* Inventory & Metrics */}
      <Route
        path="/inventory"
        element={
          <ProtectedRoute>
            <Inventory />
          </ProtectedRoute>
        }
      />
      <Route
        path="/metrics"
        element={
          <ProtectedRoute>
            <Metrics />
          </ProtectedRoute>
        }
      />
      <Route
        path="/customers"
        element={
          <ProtectedRoute>
            <Customers />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profit-reports"
        element={
          <ProtectedRoute>
            <ProfitReports />
          </ProtectedRoute>
        }
      />
      <Route
        path="/customer-intakes"
        element={
          <ProtectedRoute>
            <CustomerIntakes />
          </ProtectedRoute>
        }
      />

      {/* Mobile Phone Trading Module */}
      <Route
        path="/mobile-trading"
        element={
          <ProtectedRoute>
            <MobileTrading />
          </ProtectedRoute>
        }
      />
      <Route
        path="/mobile-trading/buy"
        element={
          <ProtectedRoute>
            <BuyDevice />
          </ProtectedRoute>
        }
      />
      <Route
        path="/mobile-trading/device/:id"
        element={
          <ProtectedRoute>
            <DeviceDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/mobile-trading/declined"
        element={
          <ProtectedRoute>
            <DeclinedIntakes />
          </ProtectedRoute>
        }
      />

      {/* IT Equipment Trading Module */}
      <Route
        path="/it-equipment"
        element={
          <ProtectedRoute>
            <ITEquipment />
          </ProtectedRoute>
        }
      />
      <Route
        path="/it-equipment/buy"
        element={
          <ProtectedRoute>
            <BuyDevice />
          </ProtectedRoute>
        }
      />
      <Route
        path="/it-equipment/device/:id"
        element={
          <ProtectedRoute>
            <DeviceDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/it-equipment/declined"
        element={
          <ProtectedRoute>
            <DeclinedIntakes />
          </ProtectedRoute>
        }
      />

      <Route
        path="/billing"
        element={
          <ProtectedRoute>
            <Billing />
          </ProtectedRoute>
        }
      />

      {/* Super Admin Routes */}
      <Route path="/super-admin" element={<SuperAdminLogin />} />
      <Route path="/super-admin/dashboard" element={<SuperAdminDashboard />} />

      {/* Customer Portal - Public Pages */}
      <Route path="/portal" element={<CustomerPortal />} />
      <Route path="/book" element={<BookAppointment />} />
      <Route path="/track" element={<TrackJob />} />
      <Route path="/history" element={<RepairHistory />} />
      <Route path="/intake" element={<CustomerIntake />} />

      {/* Legacy Public Tracking (with token) */}
      <Route path="/track/:jobNumber/:trackingToken" element={<PublicTrack />} />

      {/* Legal Pages (Public) */}
      <Route path="/legal/:pageType" element={<LegalPage />} />

      {/* Contact Page (Public) */}
      <Route path="/contact" element={<Contact />} />

      {/* SEO - Comparison Pages */}
      <Route path="/compare/:competitor" element={<ComparePage />} />
      
      {/* SEO - Feature Landing Pages */}
      <Route path="/features/:feature" element={<FeaturePage />} />

      {/* SEO - City Landing Pages */}
      <Route path="/city/:city" element={<CityLandingPage />} />

      {/* Reseller Portal Routes */}
      <Route path="/reseller/login" element={<ResellerLogin />} />
      <Route path="/reseller/register" element={<ResellerRegister />} />
      <Route path="/reseller/dashboard" element={<ResellerDashboard />} />
      <Route path="/reseller" element={<Navigate to="/reseller/dashboard" replace />} />

      {/* Retailer Portal Routes (B2B) */}
      <Route path="/retailer" element={
        <RetailerAuthProvider>
          <RetailerAuth />
        </RetailerAuthProvider>
      } />
      <Route path="/retailer/*" element={
        <RetailerAuthProvider>
          <Routes>
            <Route element={<RetailerLayout />}>
              <Route path="dashboard" element={<RetailerDashboard />} />
              <Route path="forward-job" element={<ForwardJob />} />
              <Route path="jobs" element={<RetailerJobs />} />
              <Route path="partners" element={<PartnerShops />} />
            </Route>
          </Routes>
        </RetailerAuthProvider>
      } />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <ResellerProvider>
          <BrowserRouter>
            <AppRoutes />
            <Toaster position="top-right" richColors />
          </BrowserRouter>
        </ResellerProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
